//@flow
import React from 'react';
import * as Immutable from 'immutable';
import Dialog from 'common/components/core/dialog';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import { SingleTeamSelectWithPopover } from 'common/entity/components/team';

import defaultTheme from './TeamHomebaseSelectorModal.scss';
import popOverFactory, {
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';

import { SelectTypes } from 'common/components/core/select';
import { Homebase } from 'common/models/internal/Homebase';
import type { Team } from 'common/models/internal/Team';
import type { List } from 'immutable';
import type { TFunction } from 'react-i18next';
import { HomebaseSelector } from 'common/components/core/autocomplete/HomebaseSelector';
import { personnelActiveTeamValuesSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import { homebase as fetchHomebasesApi } from 'common/middleware/api';
import { Loader } from 'common/components/core/loader/Loader';
import { SizeEnum } from 'common/enums/UIEnums';
import entityIds from 'common/tests/entity/entityIds.json';
import DataUtils from 'common/utils/DataUtils';

const HomebaseSelectorWithPopover = popOverFactory({
    Component: HomebaseSelector
});

type Props = {
    theme: Object,
    teams: List<Team>,
    clientId: string,
    defaultHomebaseId?: ?string,
    defaultTeamId?: ?string,
    title: string,
    subtitle: string,
    acceptActionLabel: ?string,
    onCreate: ({
        clientId: string,
        homebaseId: ?string,
        teamId: ?string
    }) => void,
    onCancel: () => void,
    t: TFunction
};
type State = {
    teamId: ?string,
    homebaseId: ?string,
    homebaseIdValidationMessage: ?string,
    teamIdValidationMessage: ?string,
    isFetchingHomebases: boolean,
    homebases: List<Homebase>
};

class TeamHomebaseSelectorModal extends React.PureComponent<Props, State> {
    state = {
        homebaseId: this.props.defaultHomebaseId,
        teamId: this.props.defaultTeamId,
        teamIdValidationMessage: null,
        homebaseIdValidationMessage: null,
        isFetchingHomebases: false,
        homebases: Immutable.List()
    };

    handleTeamSelect = ({ value }) => {
        this.setState(
            {
                teamId: value,
                teamIdValidationMessage: null,
                homebaseIdValidationMessage: null,
                homebaseId: null
            },
            this.fetchHomebases
        );
    };

    handleHomebaseSelect = ({ value }) => {
        this.setState({
            homebaseId: value,
            homebaseIdValidationMessage: null
        });
    };

    isCreateRequestValid = () => {
        const { homebaseId, teamId, homebases } = this.state;
        const { t, teams } = this.props;
        if (!homebaseId || !teamId) {
            let teamIdValidationMessage = null;
            let homebaseIdValidationMessage = null;
            const isTeamEmpty = DataUtils.isEmpty(teamId);
            if (isTeamEmpty) {
                teamIdValidationMessage = t('validations:noTeam');
            }
            if (!isTeamEmpty && homebases.isEmpty()) {
                const team = teams.find(
                    currentTeam => currentTeam.teamId === teamId
                );
                homebaseIdValidationMessage = t(
                    'validations:noHomebaseAssociated',
                    {
                        team: team.name //@TODO: Fix flow issue as team cant be undefined
                    }
                );
            } else if (!homebaseId) {
                homebaseIdValidationMessage = t('validations:noHomebase');
            }
            this.setState({
                teamIdValidationMessage,
                homebaseIdValidationMessage
            });
            return false;
        }
        return true;
    };

    handleAccept = () => {
        const { homebaseId, teamId } = this.state;
        const { clientId, onCreate } = this.props;
        const isValid = this.isCreateRequestValid();
        isValid && onCreate && onCreate({ homebaseId, teamId, clientId });
    };

    handleCancel = () => {
        const { onCancel } = this.props;
        onCancel && onCancel();
    };

    fetchHomebases = () => {
        const { clientId } = this.props;
        const { teamId: team } = this.state;
        this.setState({
            isFetchingHomebases: true,
            homebases: Immutable.List()
        });
        fetchHomebasesApi.fetch({ clientId, team }).then(response => {
            const homebases = Immutable.List(
                response.map(homebase => Homebase.fromJS(homebase))
            );
            this.setState({ homebases, isFetchingHomebases: false });
        });
    };

    render() {
        const {
            theme,
            teams,
            onCancel,
            title,
            subtitle,
            acceptActionLabel,
            t
        } = this.props;
        const {
            homebaseId,
            teamId,
            homebaseIdValidationMessage,
            teamIdValidationMessage,
            isFetchingHomebases,
            homebases
        } = this.state;
        const selectedHomebase =
            homebases && homebases.find(homebase => homebase.id === homebaseId);
        const selectedHomebaseAddress =
            (selectedHomebase && selectedHomebase.getAddressString()) || null;
        const isCreateDisabled =
            DataUtils.isEmpty(teamId) || homebases.isEmpty();
        return (
            <Dialog
                title={title}
                theme={theme}
                active
                acceptActionLabel={acceptActionLabel}
                onAccept={this.handleAccept}
                onCancel={onCancel}
                onDismiss={onCancel}
            >
                {isFetchingHomebases ? <Loader active theme={theme} /> : null}
                <div className={theme.contentTitle}>{subtitle}</div>
                <div className={theme.selectorsWrap}>
                    <SingleTeamSelectWithPopover
                        testId={entityIds.selectTeam}
                        selectSize={SizeEnum.Large}
                        teams={teams}
                        isPopOverOpen={!!teamIdValidationMessage}
                        placeholder={t('translations:selectTeam')}
                        place={PopoverPlaceEnum.Above}
                        popOverContent={teamIdValidationMessage}
                        theme={theme}
                        isClearable={false}
                        onChange={this.handleTeamSelect}
                        value={teamId}
                        className={theme.teamSelector}
                    />
                    <HomebaseSelectorWithPopover
                        testId={entityIds.selectHomeBase}
                        showHomebaseIcon
                        selectSize={SizeEnum.Large}
                        isPopOverOpen={!!homebaseIdValidationMessage}
                        place={PopoverPlaceEnum.Above}
                        popOverContent={homebaseIdValidationMessage}
                        onChange={this.handleHomebaseSelect}
                        selectedAddress={selectedHomebaseAddress}
                        homebases={homebases}
                        isClearable={false}
                        placeholder={t('translations:selectHomebase')}
                        theme={theme}
                        selectType={SelectTypes.Raised}
                        disabled={isCreateDisabled}
                    />
                </div>
            </Dialog>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const teams = personnelActiveTeamValuesSelector(state, ownProps);
    return {
        clientId,
        teams
    };
};

const withRedux = connect(mapStateToProps);

export default compose(
    withRouter,
    withRedux,
    themr('TeamHomebaseSelectorModal', defaultTheme),
    translate(['translations', 'validations'])
)(TeamHomebaseSelectorModal);
